import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import TableCard from 'src/components/Table/TableCard';
import { DetailCard } from 'src/components/summary/DetailCard';
import { SummaryGrid } from 'src/components/summary/SummaryGrid';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import SectionTable from 'src/features/detailSections/SectionTable';
import {
  useInfiniteWin32AppFileSection,
  useWin32AppFileDetail,
  useWin32AppFileSigners,
} from 'src/hooks/wdac';
import { formatBytes } from 'src/utils/helpers';
import { formatDate } from 'src/utils/formatTime';
import { getWdacStatusLabel } from 'src/utils/enums/wdacStatus';

const PATHS_SECTION = {
  key: 'paths',
  label: 'Paths',
  itemsKey: 'results',
  emptyMessage: 'This file has no recorded paths.',
  columns: [{ key: 'path', label: 'Full File Path', width: '100%', render: path => path }],
};

const renderSignerHash = hash => (
  <Box component="span" sx={{ fontSize: '0.62rem', color: '#D1D5DB' }}>
    {hash || '--'}
  </Box>
);

// A certificate's validity says nothing about this one file, so only who signed it is shown, the
// publisher and issuer each by name and TBS hash. The hashes get the room and smaller type, since
// they are long and read rather than scanned.
const SIGNERS_SECTION = {
  key: 'signers',
  label: 'Signers',
  emptyMessage: 'This file is unsigned.',
  columns: [
    { key: 'publisherName', label: 'Publisher Name', width: '18%' },
    {
      key: 'tbsHash',
      label: 'Publisher TBS Hash',
      width: '32%',
      render: signer => renderSignerHash(signer.tbsHash),
    },
    { key: 'issuerName', label: 'Issuer Name', width: '18%' },
    {
      key: 'issuerTbsHash',
      label: 'Issuer TBS Hash',
      width: '32%',
      render: signer => renderSignerHash(signer.issuerTbsHash),
    },
  ],
};

const DEVICES_SECTION = {
  key: 'devices',
  label: 'Devices',
  subtitle:
    'Devices that reported this file within the last 90 days, excluding archived and revoked',
  itemsKey: 'results',
  emptyMessage: 'No devices have reported this file in the last 90 days.',
  columns: [
    { key: 'name', label: 'Device Name', width: '46%' },
    {
      key: 'enforcementMode',
      label: 'Enforcement',
      width: '22%',
      render: device => getWdacStatusLabel(device.enforcementMode),
    },
    { key: 'lastEventSystemTime', label: 'Last Reported', width: '32%', date: true },
  ],
};

const HASH_FIELDS = [
  { key: 'sha256', label: 'SHA-256' },
  { key: 'authenticodeHashSha256', label: 'Authenticode SHA-256' },
];

// The title carries the total once it is known rather than flashing a zero.
const SectionCard = ({ section, query, total, enabled, maxHeight }) => (
  <TableCard
    title={total == null ? section.label : `${section.label} (${total.toLocaleString()})`}
    subtitle={section.subtitle}
    maxHeight={maxHeight}>
    <SectionTable section={section} query={query} enabled={enabled} />
  </TableCard>
);

// One file of an app: what it is, then the devices that report it, the paths it sits at and who
// signs it.
const Win32FileDetailModal = ({ open, onClose, globalApplicationVersionId, fileId }) => {
  const queryArgs = { globalApplicationVersionId, fileId, enabled: open };
  const { data: file, isLoading } = useWin32AppFileDetail(queryArgs);
  const devicesQuery = useInfiniteWin32AppFileSection({ ...queryArgs, section: 'devices' });
  const pathsQuery = useInfiniteWin32AppFileSection({ ...queryArgs, section: 'paths' });
  const signersQuery = useWin32AppFileSigners(queryArgs);

  // Devices come most recent first, so the first one is when and where the file was last seen.
  const latestDevice = devicesQuery.data?.pages?.[0]?.results?.[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        {file?.fileName || 'File'}
        {file ? (
          <Box component="span" sx={{ ml: 1, fontSize: '0.8rem', color: '#9CA3AF' }}>
            ({file.isCovered ? 'Covered by App' : 'Not Covered by App'})
          </Box>
        ) : null}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {isLoading || !file ? (
            <Skeleton variant="rounded" height={160} sx={{ bgcolor: '#484848' }} />
          ) : (
            <DetailCard compact>
              <Stack spacing={2}>
                {HASH_FIELDS.map(field => (
                  <SummaryValue key={field.key} label={field.label} compact>
                    {file[field.key]}
                  </SummaryValue>
                ))}
                <SummaryGrid compact>
                  <SummaryValue label="Original File Name" compact>
                    {file.originalFileName}
                  </SummaryValue>
                  <SummaryValue label="Product Name" compact>
                    {file.productName}
                  </SummaryValue>
                  <SummaryValue label="Version" compact>
                    {file.fileVersion}
                  </SummaryValue>
                  <SummaryValue label="Description" compact>
                    {file.fileDescription}
                  </SummaryValue>
                  <SummaryValue label="Size" compact>
                    {file.fileSize ? formatBytes(Number(file.fileSize)) : null}
                  </SummaryValue>
                  <SummaryValue label="Last Seen" compact>
                    {latestDevice
                      ? `${formatDate(latestDevice.lastEventSystemTime)} (${latestDevice.name})`
                      : devicesQuery.data
                        ? 'Not seen in the last 90 days'
                        : null}
                  </SummaryValue>
                </SummaryGrid>
              </Stack>
            </DetailCard>
          )}

          <SectionCard
            key={`${fileId}-devices`}
            section={DEVICES_SECTION}
            query={devicesQuery}
            total={devicesQuery.data?.pages?.[0]?.totalCount}
            enabled={open}
            maxHeight={260}
          />
          <SectionCard
            key={`${fileId}-paths`}
            section={PATHS_SECTION}
            query={pathsQuery}
            total={pathsQuery.data?.pages?.[0]?.totalCount}
            enabled={open}
            maxHeight={220}
          />
          {/* Full width, since each signer carries two full hashes. */}
          <SectionCard
            key={`${fileId}-signers`}
            section={SIGNERS_SECTION}
            query={signersQuery}
            total={signersQuery.data?.length}
            enabled={open}
            maxHeight={220}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default Win32FileDetailModal;
